import {
  IApIndustryOption,
  IIndustryAllocationLocal,
  IIndustryBlend,
  IIndustryBlendLocal,
  INDUSTRY_PROVIDERS,
  IProviderIndustryOption,
} from 'utils/types';

export const getFilteredOptions = ({
  apIndustry,
  gartnerIndustry,
  computerEconomicsIndustry,
  apIndustriesOptions,
  computerEconomicsIndustriesOptions,
  gartnerIndustriesOptions,
}: {
  apIndustry?: string;
  gartnerIndustry?: string;
  computerEconomicsIndustry?: string;
  apIndustriesOptions: IApIndustryOption[];
  gartnerIndustriesOptions: IProviderIndustryOption[];
  computerEconomicsIndustriesOptions: IProviderIndustryOption[];
}) => {
  if (!apIndustry && !gartnerIndustry && !computerEconomicsIndustry) {
    return {
      apIndustriesOptions,
      computerEconomicsIndustriesOptions,
      gartnerIndustriesOptions,
    };
  }
  if (apIndustry) {
    return {
      apIndustriesOptions,
      computerEconomicsIndustriesOptions:
        computerEconomicsIndustriesOptions.filter(
          (option) => option.apIndustryL1Code === apIndustry
        ),
      gartnerIndustriesOptions: gartnerIndustriesOptions.filter(
        (option) => option.apIndustryL1Code === apIndustry
      ),
    };
  }
  if (gartnerIndustry) {
    const gartnerOption = gartnerIndustriesOptions.find(
      (option) => option.value === gartnerIndustry
    );
    const apIndustriesFilteredOptions = apIndustriesOptions.filter(
      (option) => option.value === gartnerOption?.apIndustryL1Code
    );
    const apCodes = apIndustriesFilteredOptions.map((opt) => opt.value);
    const computerEconomicsIndustriesFilteredOptions =
      computerEconomicsIndustriesOptions.filter((option) =>
        apCodes.includes(option.apIndustryL1Code)
      );
    return {
      apIndustriesOptions: apIndustriesFilteredOptions,
      computerEconomicsIndustriesOptions:
        computerEconomicsIndustriesFilteredOptions,
      gartnerIndustriesOptions: gartnerIndustriesOptions.filter(
        (option) => option.apIndustryL1Code === apIndustry
      ),
    };
  }
  return {
    apIndustriesOptions,
    computerEconomicsIndustriesOptions,
    gartnerIndustriesOptions,
  };
};

export const blendLocalToAPI = (
  blend: IIndustryBlendLocal
): IIndustryBlend => ({
  ...blend,
  revenueUpliftPercentage: blend.revenueUpliftPercentage
    ? Number(blend.revenueUpliftPercentage)
    : undefined,
  industryAllocations: blend.industryAllocations.map((allocation) => {
    return {
      allocationPercentage: allocation.allocationPercentage,
      apIndustryCode: allocation.apIndustryCode,
      providerIndustries: [
        {
          provider: INDUSTRY_PROVIDERS.COMPUTER_ECONOMICS,
          code: allocation.computerEconomicsIndustryCode,
        },
        {
          provider: INDUSTRY_PROVIDERS.GARTNER,
          code: allocation.gartnerIndustryCode,
        },
      ],
    };
  }),
});

export const blendAPIToLocal = (
  blend: IIndustryBlend
): IIndustryBlendLocal => ({
  ...blend,
  revenueUpliftPercentage:
    blend.revenueUpliftPercentage === null
      ? ''
      : String(blend.revenueUpliftPercentage),
  industryAllocations: blend.industryAllocations.map((industry) => {
    const computerEconomicsIndustry = industry.providerIndustries.find(
      (obj) => obj.provider === INDUSTRY_PROVIDERS.COMPUTER_ECONOMICS
    );
    const gartnerIndustry = industry.providerIndustries.find(
      (obj) => obj.provider === INDUSTRY_PROVIDERS.GARTNER
    );
    return {
      ...industry,
      gartnerIndustryCode: gartnerIndustry ? gartnerIndustry.code : '',
      computerEconomicsIndustryCode: computerEconomicsIndustry
        ? computerEconomicsIndustry.code
        : '',
    };
  }),
});

export const isValidLocalBlend = (blend?: IIndustryBlendLocal) => {
  if (!blend) {
    return false;
  }
  const { industryAllocations, name, revenueUpliftPercentage, year } = blend;
  if (!name || !year) {
    return false;
  }
  const numberRevenueUpliftPercentage = Number(revenueUpliftPercentage);
  if (
    revenueUpliftPercentage !== '' &&
    (numberRevenueUpliftPercentage < 1 || numberRevenueUpliftPercentage > 25)
  ) {
    return false;
  }
  if (getTotalAllocation(industryAllocations) !== 100) {
    return false;
  }
  return industryAllocations.every((industry) => {
    if (
      !industry.apIndustryCode ||
      !industry.computerEconomicsIndustryCode ||
      !industry.gartnerIndustryCode
    ) {
      return false;
    }
    return true;
  });
};

export const getTotalAllocation = (industries: IIndustryAllocationLocal[]) =>
  industries.reduce((acc, industry) => {
    return acc + industry.allocationPercentage;
  }, 0);

export const getYearInputHint = (year: number | string) =>
  `The latest available benchmark year is ${year}`;

export const REVENUE_UPLIFT_INPUT_HINT =
  'Benchmark metric uplift is only applicable for IT Spend as a % of Revenue and Total IT Spending as a Percentage of Revenue benchmarks. You can apply an uplift of up to 25%.';
